import AddIcon from '@mui/icons-material/Add'
import BusinessIcon from '@mui/icons-material/Business'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Box, Button, Paper, styled, Typography } from '@mui/material'
import type { ReactNode } from 'react'

const EmptyWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(10),
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  textAlign: 'center',
}))

const IconBox = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 16,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2.5),
  '& .MuiSvgIcon-root': {
    fontSize: 40,
    color: theme.palette.text.disabled,
  },
}))

/** The reasons the organization list can come back with nothing to render. */
export type OrgEmptyStateVariant = 'firstRun' | 'noSearchResults' | 'pageOutOfRange'

interface EmptyStateContent {
  icon: ReactNode
  title: string
  description: string
}

const VARIANT_CONTENT: Record<OrgEmptyStateVariant, EmptyStateContent> = {
  firstRun: {
    icon: <BusinessIcon />,
    title: 'No organizations found',
    description: 'Get started by creating your first organization',
  },
  noSearchResults: {
    icon: <SearchOffIcon />,
    title: 'No matching organizations',
    description: 'Try a different name or organization ID.',
  },
  pageOutOfRange: {
    icon: <FirstPageIcon />,
    title: 'Nothing on this page',
    description: 'This page is past the end of the directory.',
  },
}

interface OrgEmptyStateProps {
  /**
   * Why the list is empty, which decides both the copy and the action offered.
   * Only a genuinely empty directory invites creating an organization: a search
   * that matched nothing and a page past the end of the results both mean
   * organizations exist, just not here.
   */
  variant: OrgEmptyStateVariant
  onCreateOrg: () => void
  onResetPage: () => void
}

export const OrgEmptyState = ({ variant, onCreateOrg, onResetPage }: OrgEmptyStateProps) => {
  const { icon, title, description } = VARIANT_CONTENT[variant]
  const hasAction = variant !== 'noSearchResults'

  return (
    <EmptyWrapper elevation={0}>
      <IconBox>{icon}</IconBox>
      <Typography variant='h5' gutterBottom>
        {title}
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={hasAction ? 3 : 0}>
        {description}
      </Typography>
      {variant === 'firstRun' && (
        <Button variant='contained' size='large' startIcon={<AddIcon />} onClick={onCreateOrg}>
          Create Organization
        </Button>
      )}
      {variant === 'pageOutOfRange' && (
        <Button
          variant='outlined'
          size='large'
          startIcon={<FirstPageIcon />}
          onClick={onResetPage}
        >
          Back to First Page
        </Button>
      )}
    </EmptyWrapper>
  )
}
